import type { Data } from "@eai-toolkit/client";
import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { Dispatch, SetStateAction } from "react";

import type { FieldName } from "../../modules/data-explorer/data/fields";
import {
  fetchFileSourceUrl,
  fetchText,
  fetchTiffImageData,
  ITEMS_PER_PAGE,
  listData,
  prefetchData,
} from "../../modules/data-explorer/helpers/api";
import {
  DefaultFieldSort,
  toggleSortDirection,
} from "../../modules/data-explorer/helpers/utils";
import type { RootState } from "../store";

interface DataList {
  items: Data[];
  metadata?: {
    total: number;
  };
}

interface CachedData {
  data: DataList;
  timestamp: number;
}

export interface DataState {
  list: DataList | null;
  loading: boolean;
  error: string | null;
  ready: boolean;
  fileContent: { content: string; isComplete: boolean } | null;
  fileUrl: string | null;
  tiffData: { imageData: ImageData; width: number; height: number } | null;
  cachedPages: {
    [key: string]: CachedData;
  };
  isPrefetching: boolean;
}

const initialState: DataState = {
  list: null,
  loading: false,
  error: null,
  ready: true,
  fileContent: null,
  fileUrl: null,
  tiffData: null,
  cachedPages: {},
  isPrefetching: false,
};

export const CACHE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

const generateCacheKey = (
  page: number,
  searchFields?: { [key: string]: string },
  fieldSortedBy?: FieldName | null,
  fieldSortDirection?: "asc" | "desc",
  permission?: "data:get" | "data:write",
) => {
  const searchRepresentation = JSON.stringify(searchFields);
  return `${page}-${searchRepresentation}-${fieldSortedBy}-${fieldSortDirection}-${permission}`;
};

export const fetchDataList = createAsyncThunk(
  "data/fetchList",
  async (
    {
      page,
      searchFields = {},
      searchTerm = "",
      fieldSortedBy = null,
      fieldSortDirection = "asc",
      permission = "data:get",
    }: {
      page: number;
      searchFields?: { [key: string]: string };
      searchTerm?: string;
      fieldSortedBy?: FieldName | null;
      fieldSortDirection?: "asc" | "desc";
      permission?: "data:get" | "data:write";
    },
    { getState, dispatch },
  ) => {
    const state = getState() as RootState;
    if (searchTerm) {
      searchFields["name"] = searchTerm;
    }

    const cacheKey = generateCacheKey(
      page,
      searchFields,
      fieldSortedBy,
      fieldSortDirection,
      permission,
    );
    const cachedData = state.data.cachedPages[cacheKey];
    if (cachedData && Date.now() - cachedData.timestamp < CACHE_TIMEOUT) {
      return cachedData.data;
    }
    // TODO: is this true for ID? Should another column be sorted on initially?
    // Since it's common for users to reverse direction right away, we grab both the requested sort direction
    // and the reverse direction and just cache the reverse, in case we need it again soon
    const reverseSortDirection = toggleSortDirection(fieldSortDirection);
    const reverseCacheKey = generateCacheKey(
      page,
      searchFields,
      fieldSortedBy,
      reverseSortDirection,
      permission,
    );
    const reverseResponse = await listData(
      "listData",
      page,
      searchFields,
      fieldSortedBy,
      reverseSortDirection,
      permission,
    );

    if (reverseResponse) {
      dispatch(
        dataSlice.actions.addToCache({
          key: reverseCacheKey,
          data: {
            items: reverseResponse.items,
            metadata: reverseResponse.metadata,
          },
          timestamp: Date.now(),
        }),
      );

      const response = await listData(
        "listData",
        page,
        searchFields,
        fieldSortedBy,
        fieldSortDirection,
        permission,
      );
      return response;
    }
  },
);

export const fetchFileContent = createAsyncThunk(
  "data/fetchFileContent",
  async ({ dataId, filepath }: { dataId: string; filepath: string }) => {
    const response = await fetchText("fileContent", dataId, filepath);
    return response;
  },
);

export const fetchFileUrl = createAsyncThunk(
  "data/fetchFileUrl",
  async ({ dataId, mapping }: { dataId: string; mapping: string }) => {
    const response = await fetchFileSourceUrl("fileUrl", dataId, mapping);
    return response;
  },
);

export const fetchTiffData = createAsyncThunk(
  "data/fetchTiffData",
  async ({ dataId, filename }: { dataId: string; filename: string }) => {
    const response = await fetchTiffImageData("tiffData", dataId, filename);
    return response;
  },
);

export const prefetchListData = createAsyncThunk(
  "data/prefetchList",
  async (
    setReady: Dispatch<SetStateAction<boolean | number>>,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState;
    if (state.data.isPrefetching) {
      return;
    }

    dispatch(dataSlice.actions.setPrefetching(true));

    try {
      const data = await prefetchData(
        setReady,
        0,
        DefaultFieldSort.fieldName,
        DefaultFieldSort.direction,
      );
      setReady(false);

      // Break up the data into individual pages and add each to cache
      if (data?.items?.length) {
        for (let i = 0; i < data.items.length; i += ITEMS_PER_PAGE) {
          const pageItems = data.items.slice(i, i + ITEMS_PER_PAGE);
          const pageNumber = i / ITEMS_PER_PAGE;
          const cacheKey = generateCacheKey(
            pageNumber,
            {},
            DefaultFieldSort.fieldName,
            DefaultFieldSort.direction,
          );

          // Dispatch an action to add this page to the cache immediately
          dispatch(
            dataSlice.actions.addToCache({
              key: cacheKey,
              data: {
                items: pageItems,
                metadata: data.metadata,
              },
              timestamp: Date.now(),
            }),
          );
        }
      }
      setReady(true);
    } finally {
      dispatch(dataSlice.actions.setPrefetching(false));
    }
  },
);

export const getAllCachedItems = (state: RootState) => {
  const allItems: Data[] = [];
  Object.values(state.data.cachedPages).forEach((cachedData: CachedData) => {
    allItems.push(...cachedData.data.items);
  });
  return allItems;
};

const dataSlice = createSlice({
  name: "data",
  initialState,
  reducers: {
    setReady: (state, action) => {
      state.ready = action.payload;
    },
    clearCache: (state) => {
      state.cachedPages = {};
    },
    clearFileData: (state) => {
      state.fileContent = initialState.fileContent;
      state.fileUrl = initialState.fileUrl;
      state.tiffData = initialState.tiffData;
    },
    addToCache: (state, action) => {
      const { key, data, timestamp } = action.payload;
      state.cachedPages[key] = {
        data,
        timestamp,
      };
    },
    setPrefetching: (state, action) => {
      state.isPrefetching = action.payload;
    },
    updateDataObjectInList: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Data> }>,
    ) => {
      const { id, updates } = action.payload;

      // Update in current list
      if (state.list?.items) {
        const index = state.list.items.findIndex(
          (item) => item.id === id || item.fullName === id,
        );
        if (index !== -1) {
          state.list.items[index] = { ...state.list.items[index], ...updates };
        }
      }

      // Update in all cached pages
      Object.keys(state.cachedPages).forEach((key) => {
        const cachedData = state.cachedPages[key];
        if (cachedData?.data?.items) {
          const index = cachedData.data.items.findIndex(
            (item) => item.id === id || item.fullName === id,
          );
          if (index !== -1) {
            cachedData.data.items[index] = {
              ...cachedData.data.items[index],
              ...updates,
            };
          }
        }
      });
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchDataList cases
      .addCase(fetchDataList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDataList.fulfilled, (state, action) => {
        state.loading = false;
        if (!action.payload) {
          state.list = null;
          return;
        }

        state.list = action.payload;

        if (action.meta.arg) {
          const {
            page,
            searchFields,
            fieldSortedBy,
            fieldSortDirection,
            permission,
          } = action.meta.arg;
          const cacheKey = generateCacheKey(
            page,
            searchFields,
            fieldSortedBy,
            fieldSortDirection,
            permission,
          );
          state.cachedPages[cacheKey] = {
            data: action.payload,
            timestamp: Date.now(),
          };
        }
      })
      .addCase(fetchDataList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "An error occurred";
      })
      // fetchFileContent cases
      .addCase(fetchFileContent.fulfilled, (state, action) => {
        state.fileContent = action.payload;
      })
      // fetchFileUrl cases
      .addCase(fetchFileUrl.fulfilled, (state, action) => {
        state.fileUrl = action.payload;
      })
      // fetchTiffData cases
      .addCase(fetchTiffData.fulfilled, (state, action) => {
        state.tiffData = action.payload;
      })
      // Add prefetchList cases
      .addCase(prefetchListData.fulfilled, () => {
        // No need to do anything here since we're updating the cache
        // incrementally via the addToCache action
      });
  },
});

export const {
  setReady,
  clearFileData,
  clearCache,
  addToCache,
  setPrefetching,
  updateDataObjectInList,
} = dataSlice.actions;
export default dataSlice.reducer;
